import { useChatTabsStore } from '@/stores/chatTabsStore.js';
import { useChatStore } from '@/stores/chatStore.js';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button.js';
import { X, Plus, MoreHorizontal, Loader2, CheckCircle, XCircle, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { useServices } from '@/contexts/ServicesContext.js';
import { useMobile } from '@/hooks/useMobile.js';

export function ChatTabBar() {
  const isMobile = useMobile();

  // Hide tabs on mobile - users switch via drawer
  if (isMobile) {
    return null;
  }
  const tabs = useChatTabsStore((state) => state.tabs);
  const activeTabId = useChatTabsStore((state) => state.activeTabId);
  const setActiveTab = useChatTabsStore((state) => state.setActiveTab);
  const closeTab = useChatTabsStore((state) => state.closeTab);
  const addTab = useChatTabsStore((state) => state.addTab);
  const closeOtherTabs = useChatTabsStore((state) => state.closeOtherTabs);
  const closeAllTabs = useChatTabsStore((state) => state.closeAllTabs);
  const sessions = useChatStore((state) => state.sessions);
  const navigate = useNavigate();
  const { sessionService } = useServices();

  const handleTabClick = (tabId: string, sessionId: string | null) => {
    setActiveTab(tabId);
    if (sessionId) {
      navigate(`/chat/${sessionId}`);
    } else {
      sessionService.returnToWelcome();
      navigate('/');
    }
  };

  const handleCloseTab = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation();

    closeTab(tabId);

    // If closing active tab, navigate appropriately
    if (activeTabId === tabId) {
      // Store will create a new welcome tab if this was the last one
      const newActiveTab = tabs.find((t) => t.id !== tabId);
      if (newActiveTab) {
        if (newActiveTab.sessionId) {
          navigate(`/chat/${newActiveTab.sessionId}`);
        } else {
          sessionService.returnToWelcome();
          navigate('/');
        }
      } else {
        // This was the last tab, navigate to welcome (new tab will be auto-created)
        sessionService.returnToWelcome();
        navigate('/');
      }
    }
  };

  const handleNewTab = () => {
    // Generate unique ID for each new welcome tab
    const uniqueId = `welcome-${Date.now()}`;
    addTab({
      id: uniqueId,
      title: 'New Chat',
      sessionId: null,
      isPinned: false,
    });
    sessionService.returnToWelcome();
    navigate('/');
  };

  const handleCloseAllTabs = () => {
    closeAllTabs();
    sessionService.returnToWelcome();
    navigate('/');
  };

  return (
    <div className="flex items-center gap-1 pt-2 bg-muted/20">
      {/* Tabs */}
      <div className="flex-1 flex items-center gap-1 min-w-0 overflow-x-auto scrollbar-thin">
        {tabs.map((tab) => {
          const isActive = activeTabId === tab.id;
          // Check if this tab's session is currently processing
          const isProcessing = tab.sessionId ? (sessions.get(tab.sessionId)?.processing || false) : false;
          // Check tab status for scheduled tasks
          const isScheduleTab = !!tab.scheduleId;
          const isRunning = tab.status === 'running';
          const isCompleted = tab.status === 'completed';
          const isFailed = tab.status === 'failed';

          return (
            <div
              key={tab.id}
              onClick={() => handleTabClick(tab.id, tab.sessionId)}
              className={cn(
                'group relative flex items-center gap-2 px-4 py-2 cursor-pointer transition-all flex-shrink-0',
                'rounded-t border-t border-x',
                isActive
                  ? 'bg-background border-border'
                  : 'bg-transparent border-transparent hover:bg-muted/50',
                // Status-based border colors
                isRunning && 'border-yellow-400/50',
                isCompleted && 'border-green-400/50',
                isFailed && 'border-red-400/50'
              )}
            >
              {/* Status indicator */}
              {isRunning ? (
                <Loader2 className="h-3 w-3 animate-spin text-yellow-500 flex-shrink-0" />
              ) : isCompleted ? (
                <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
              ) : isFailed ? (
                <XCircle className="h-3 w-3 text-red-500 flex-shrink-0" />
              ) : isScheduleTab ? (
                <Calendar className="h-3 w-3 text-muted-foreground flex-shrink-0" />
              ) : isProcessing && !isActive ? (
                <Loader2 className="h-3 w-3 animate-spin text-primary flex-shrink-0" />
              ) : null}
              <span className={cn('text-sm font-medium truncate max-w-[180px]', isActive ? 'text-foreground' : 'text-muted-foreground')}>
                {tab.title}
              </span>
              <button
                onClick={(e) => handleCloseTab(e, tab.id)}
                className="opacity-0 group-hover:opacity-100 hover:bg-muted/50 rounded p-1 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0 pb-2">
        {/* New Tab */}
        <Button variant="ghost" size="icon-sm" onClick={handleNewTab} title="New Tab (Alt+T)">
          <Plus className="h-4 w-4" />
        </Button>

        {/* More Options */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleNewTab}>New Tab</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => activeTabId && closeOtherTabs(activeTabId)}
              disabled={tabs.length <= 1}
            >
              Close Other Tabs
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleCloseAllTabs} disabled={tabs.length === 1}>
              Close All Tabs
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
